import type { icModel } from 'generated-ts-common';
import { intl, intlCountry } from '../../../lib';
import { isEmptyString } from '../../../util';

type EnhancedPatient = icModel.saf.infrastructure.model.patient.EnhancedPatient;
type Address = icModel.saf.infrastructure.model.patient.Address;

export type PatientDetailsPatientProp = {
  /**
   * The patient whose details are being displayed.
   */
  readonly patient: Partial<EnhancedPatient>;
  readonly showLabel?: boolean;
  readonly showDeathInfo?: boolean;
};

/**
 * Returns "<Unknown>" string.
 */
export const getPatientDetailsUnknownString = (): string =>
  '<' + intl('common.unknown') + '>';

/**
 * Function to simply get "<Unknown>" if the given value is not set or empty.
 * Otherwise it returns the passed value.
 *
 * @param value
 *        value to be checked
 */
export const getValueOrUnknownString = (value: string | undefined): string => {
  if (!value || isEmptyString(value)) {
    return getPatientDetailsUnknownString();
  }
  return value;
};

/**
 * Helper method to get the full version of a patient's gender.
 *
 * @param gender
 *        short version of gender
 */
export const getFullGender = (gender: string | undefined): string => {
  switch (gender) {
    case 'M':
      return intl('gender.M');
    case 'F':
      return intl('gender.F');
    case 'O':
      return intl('gender.O');
    case 'U':
      return intl('gender.U');
    default:
      return '';
  }
};

/**
 * Helper method to format the birthplace in form of "Country, City".
 * If one of City or Country is not present, the other will be displayed by itself.
 *
 * @param birthPlace
 *        contains the address data
 */
export const formattedBirthPlace = (birthPlace: Address): string => {
  const birthCountry = intlCountry(birthPlace.country);
  const birthCity = birthPlace.city;

  return birthCountry && birthCity
    ? `${birthCountry}, ${birthCity}`
    : birthCountry || birthCity;
};

/**
 * Helper method to get the full name of a patient in the form of "[Given name] [Family name]".
 * For example, "John Doe". If one of the names is missing, the available one is returned. For example, "John".
 *
 * @param patient
 *        The patient whose name we want.
 */
export const getPatientFullName = (
  patient: Partial<EnhancedPatient>
): string | undefined => {
  const patientGivenName = patient.name?.givenName;
  const patientFamilyName = patient.name?.familyName;

  return patientGivenName && patientFamilyName
    ? `${patientGivenName} ${patientFamilyName}`
    : patientGivenName || patientFamilyName;
};
